import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { startCase } from 'lodash';
import { ITrainersData, ITrainersPage, ITrainersTool } from '@/types/trainers';
import {
  getTrainersContent,
  submitBenchmark,
  submitTrainersResponse,
} from '@/api/qa';
import { useTrainerFeedback } from '@/hooks/useTrainerFeedback';
import { useTrainerReviews } from '@/hooks/useTrainerReviews';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/shadcn/dialog';
import { Button } from '@/components/shadcn/button';
import { HEADER_HEIGHT } from '@/utils/explorer';
import TrainerHeader from './TrainerHeader';
import TrainerFooter from './TrainerFooter';
import TrainerButton from './TrainerButton';
import ErrorMessage from './shared/ErrorMessage';
import Feedback from './shared/Feedback';
import TrainerController, { TrainerButtons } from './TrainerController';
import DefaultTrainerContent from './content/DefaultTrainerContent';

interface Props extends ITrainersPage {
  data?: ITrainersData & { labels?: string[] };
  tool?: ITrainersTool;
  buttonLabel?: string;
  modalTitle?: string;
}

const ConfirmOrRelabelImage: React.FC<Props> = ({
  data: initialData,
  page,
  name,
  description,
  location,
  tool,
  buttonLabel = 'Select Option',
  modalTitle = 'Choose an Option',
}) => {
  const headerRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const controllerRef = useRef<HTMLDivElement>(null);
  const toolId = initialData?.toolId || '';
  const [data, setData] = useState<ITrainersData | undefined>(initialData);
  const { leaseId } = data?.content || {};
  const isDataReady = Boolean(data?.content);
  const [isBenchmark, setIsBenchmark] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { incrementReviews } = useTrainerReviews();
  const { feedback, setFeedback } = useTrainerFeedback();

  const buttons =
    data?.content?.buttonListOverride || tool?.args?.buttonList || [];
  const items = buttons;

  const clearAllData = () => {
    setData(undefined);
    setIsBenchmark(false);
    setIsModalOpen(false);
  };

  const refetchClient = useCallback(
    async (id: string) => {
      const _data: ITrainersData = await getTrainersContent(id, page);
      setData(_data);
    },
    [page],
  );

  const onBenchmarkToggle = (checked: boolean) => {
    setIsBenchmark(checked);
  };

  const handleSubmit = async (value: string) => {
    clearAllData();
    if (!leaseId) return;

    const params = {
      leaseId,
      value: value,
    };

    if (isBenchmark) {
      await submitBenchmark(toolId, params);
    }
    const { error, benchmarkFeedback } = await submitTrainersResponse(
      toolId,
      params,
    );
    if (benchmarkFeedback) {
      setFeedback({
        ...benchmarkFeedback,
        instructionsLink: tool?.args?.instructions,
      });
    }

    if (error) {
      setData({ error });
    } else {
      incrementReviews(toolId);
      refetchClient(toolId);
    }
  };

  const handleModalItemClick = (value: string) => {
    handleSubmit(value);
    setIsModalOpen(false);
  };
  const [contentHeight, setContentHeight] = useState(0);
  useEffect(() => {
    const calculateContentHeight = () => {
      const pageHeaderHeight = HEADER_HEIGHT;
      const sectionHeaderHeight = headerRef.current?.offsetHeight ?? 0;
      const controllerHeight = controllerRef.current?.offsetHeight ?? 0;
      const footerHeight = footerRef.current?.offsetHeight ?? 0;
      const newContentHeight =
        window.innerHeight -
        pageHeaderHeight -
        sectionHeaderHeight -
        controllerHeight -
        footerHeight;
      setContentHeight(newContentHeight);
    };

    calculateContentHeight();
    window.addEventListener('resize', calculateContentHeight);

    return () => window.removeEventListener('resize', calculateContentHeight);
  }, [leaseId]);

  const otherLabels = useMemo(() => {
    return (
      data?.content?.labels?.map(label => ({
        value: label,
        label: startCase(label),
      })) || []
    );
  }, [data?.content?.labels]);

  return (
    <div className="flex size-full flex-col">
      <TrainerHeader
        data={data}
        name={name}
        description={description}
        location={location}
        tool={tool}
        elementRef={headerRef}
      />
      {data?.error ? (
        <ErrorMessage error={data.error} />
      ) : (
        <>
          <div className="flex flex-1 flex-col items-center justify-center space-y-4">
            <div className="flex flex-wrap items-center justify-center gap-4">
              <DefaultTrainerContent
                data={data}
                tool={tool}
                contentHeight={contentHeight}
              />
              <TrainerController
                containerRef={controllerRef}
                isDataReady={isDataReady}
                data={data}
                onSubmit={({ outcome }) => {
                  if (
                    outcome === 'approved' &&
                    data?.content?.elementDescription
                  ) {
                    handleModalItemClick(data.content.elementDescription);
                  } else {
                    handleModalItemClick(outcome ?? 'skip');
                  }
                }}
                tool={tool}>
                <TrainerButtons />
                <TrainerButton
                  key={'No'}
                  disabled={!isDataReady}
                  onClick={() => setIsModalOpen(true)}
                  label={'No'}
                />
              </TrainerController>
            </div>

            <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
              <DialogContent className="max-h-[80dvh] max-w-[700px]">
                <DialogHeader>
                  <DialogTitle>{modalTitle}</DialogTitle>
                </DialogHeader>
                <div className="grid grid-cols-3 gap-4 py-4">
                  {otherLabels.map(({ value, label }) => (
                    <Button
                      key={value}
                      variant="outline"
                      className="justify-start py-6 text-left text-base"
                      onClick={() => handleModalItemClick(value)}>
                      {label}
                    </Button>
                  ))}
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </>
      )}
      <TrainerFooter
        data={data}
        elementRef={footerRef}
        onBenchmarkToggle={onBenchmarkToggle}
        isBenchmark={isBenchmark}
      />
      {feedback?.feedbackMessage && <Feedback />}
    </div>
  );
};

export default ConfirmOrRelabelImage;
